/**
 * @fileoverview useRealtimeTranscription — opens an OpenAI Realtime
 * transcription session over WebSocket and streams microphone audio
 * to it, surfacing live deltas + final transcripts via callbacks.
 *
 * Why this lives in `src/ui/hooks` (not `src/hooks`):
 * the hook is consumed by the `NotesEditor` UI component and follows
 * the existing kebab-case `use-*.ts` convention used by the other
 * hooks in this folder.
 *
 * Pipeline:
 *  1. Call the `createRealtimeTranscriptionToken` action to mint a
 *     short-lived ephemeral client secret with the transcription
 *     session config (model, format, no-VAD) baked in.
 *  2. Open `wss://api.openai.com/v1/realtime` with the secret attached
 *     as a Sec-WebSocket-Protocol entry. The session config travels
 *     with the secret, so the WebSocket inherits transcription mode.
 *  3. `getUserMedia({ audio })` → an `AudioContext` at 24 kHz fed by a
 *     small inline `AudioWorklet` that down-mixes to mono PCM16 and
 *     emits ~20 ms frames base64-encoded back to the main thread.
 *  4. Each frame is forwarded as `input_audio_buffer.append`.
 *  5. Listens for `*.input_audio_transcription.delta` for live
 *     interim text and `*.input_audio_transcription.completed` for
 *     the final transcript.
 *
 * The transcription model (`gpt-realtime-whisper`) does not support
 * server-side VAD, so the browser commits the audio buffer manually
 * when the user clicks Stop — that is when the final transcript fires.
 * Live deltas continue to stream as the user dictates.
 *
 * The hook auto-stops at `maxSeconds` (default 5 minutes) so a
 * forgotten mic doesn't burn quota indefinitely.
 *
 * Cost (MOR-2689): OpenAI bills the audio by the minute, and only the
 * browser knows how much it sent, so the hook counts the frames it
 * streamed and reports their duration once when the session tears down
 * (`contacts/voiceUsage.recordDictationUsage`), best-effort.
 */

import { useAction, useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDictationEnabled } from "@/ui/hooks/use-dictation-enabled";

const REALTIME_URL = "wss://api.openai.com/v1/realtime";

/** Sample rate the OpenAI Realtime API expects for PCM16 audio. */
const TARGET_SAMPLE_RATE = 24000;

/** ~20 ms of audio at 24 kHz mono. */
const FRAME_SAMPLES = 480;

/** Hard ceiling so a forgotten mic doesn't bleed minutes of quota. */
const DEFAULT_MAX_SECONDS = 300;

/**
 * After we send `input_audio_buffer.commit` we need to wait for the
 * server to process the audio and emit the final transcript event.
 * If the event never arrives (e.g. transient server hiccup) we close
 * the socket anyway so the UI doesn't get stuck in "Processing…".
 */
const FINAL_TRANSCRIPT_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// AudioWorklet processor source (compiled into a Blob URL at runtime)
// ---------------------------------------------------------------------------
//
// The processor:
//   - down-mixes whatever channel layout the device hands us to mono,
//   - resamples to 24 kHz with a simple linear resampler — Realtime
//     transcription is robust enough that fancier resampling adds cost
//     without measurable accuracy gains for short voice notes,
//   - emits PCM16 chunks of FRAME_SAMPLES samples (~20 ms each) back
//     to the main thread for base64 encoding + WebSocket dispatch.
//
// Inline-as-string + Blob URL means we don't need a Vite worklet
// loader / public asset / extra build wiring.

const WORKLET_SOURCE = /* javascript */ `
class PcmDownsampleProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const params = options?.processorOptions ?? {};
    this.targetRate = params.targetSampleRate ?? 24000;
    this.frameSamples = params.frameSamples ?? 480;
    this._buf = new Float32Array(0);
  }

  static get parameterDescriptors() { return []; }

  _appendBuffer(arr) {
    const merged = new Float32Array(this._buf.length + arr.length);
    merged.set(this._buf, 0);
    merged.set(arr, this._buf.length);
    this._buf = merged;
  }

  _drainFrames() {
    while (this._buf.length >= this.frameSamples) {
      const frame = this._buf.subarray(0, this.frameSamples);
      const pcm16 = new Int16Array(frame.length);
      for (let i = 0; i < frame.length; i++) {
        const s = Math.max(-1, Math.min(1, frame[i]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      this.port.postMessage(pcm16.buffer, [pcm16.buffer]);
      this._buf = this._buf.slice(this.frameSamples);
    }
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;

    const channelCount = input.length;
    const frameLen = input[0].length;

    // Down-mix to mono.
    const mono = new Float32Array(frameLen);
    for (let c = 0; c < channelCount; c++) {
      const ch = input[c];
      for (let i = 0; i < frameLen; i++) {
        mono[i] += ch[i];
      }
    }
    if (channelCount > 1) {
      for (let i = 0; i < mono.length; i++) mono[i] /= channelCount;
    }

    // Linear resample from sampleRate (the AudioWorklet global) to targetRate.
    const inRate = sampleRate;
    if (inRate === this.targetRate) {
      this._appendBuffer(mono);
    } else {
      const ratio = this.targetRate / inRate;
      const outLen = Math.floor(mono.length * ratio);
      const out = new Float32Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const srcIdx = i / ratio;
        const i0 = Math.floor(srcIdx);
        const i1 = Math.min(i0 + 1, mono.length - 1);
        const frac = srcIdx - i0;
        out[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
      }
      this._appendBuffer(out);
    }

    this._drainFrames();
    return true;
  }
}

registerProcessor('pcm-downsample-processor', PcmDownsampleProcessor);
`;

let cachedWorkletUrl: string | null = null;
function getWorkletUrl(): string {
  if (cachedWorkletUrl) return cachedWorkletUrl;
  const blob = new Blob([WORKLET_SOURCE], { type: "application/javascript" });
  cachedWorkletUrl = URL.createObjectURL(blob);
  return cachedWorkletUrl;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseRealtimeTranscriptionOptions {
  /** Fires while the API emits interim transcript deltas. */
  onDelta?: (delta: string) => void;
  /**
   * Fires when the audio buffer is committed (today: when the user
   * clicks Stop, since `gpt-realtime-whisper` doesn't support VAD).
   */
  onFinal?: (transcript: string) => void;
  /** Surface non-fatal errors back to the UI. */
  onError?: (message: string) => void;
  /** Hard auto-stop. Defaults to 300 s (5 minutes). */
  maxSeconds?: number;
}

/**
 * - `idle`: no session.
 * - `listening`: mic is open, audio + deltas are streaming.
 * - `processing`: user clicked Stop, mic is closed, we're waiting for
 *   the server to emit the final transcript before closing the socket.
 */
export type TranscriptionState = "idle" | "listening" | "processing";

export interface UseRealtimeTranscription {
  start: () => Promise<void>;
  stop: () => void;
  state: TranscriptionState;
  /** True while `state` is anything other than `idle`. */
  isActive: boolean;
  error: string | null;
  /**
   * False when the deployment has dictation switched off
   * (`DICTATION_ENABLED=false`) or has not said yet: every mic control
   * hides itself.
   */
  isEnabled: boolean;
}

interface RealtimeMessage {
  type: string;
  transcript?: string;
  delta?: string;
  error?: { message?: string };
  // The full set of fields varies per event — we only read the ones above.
}

export function useRealtimeTranscription(
  options: UseRealtimeTranscriptionOptions = {},
): UseRealtimeTranscription {
  const {
    onDelta,
    onFinal,
    onError,
    maxSeconds = DEFAULT_MAX_SECONDS,
  } = options;

  const createToken = useAction(
    api.contacts.voice.createRealtimeTranscriptionToken,
  );
  const recordUsage = useMutation(api.contacts.voiceUsage.recordDictationUsage);
  const isEnabled = useDictationEnabled();

  const [state, setState] = useState<TranscriptionState>("idle");
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const awaitingFinalRef = useRef<boolean>(false);
  /** PCM16 frames sent this session, for the cost report on teardown. */
  const framesSentRef = useRef(0);

  // Keep callbacks in refs so changing them between renders doesn't
  // tear down the live session.
  const onDeltaRef = useRef(onDelta);
  const onFinalRef = useRef(onFinal);
  const onErrorRef = useRef(onError);
  onDeltaRef.current = onDelta;
  onFinalRef.current = onFinal;
  onErrorRef.current = onError;

  // Releases the mic, audio context, and worklet — but leaves the
  // WebSocket open. Used when the user clicks Stop so we can flush
  // the audio buffer and wait for the final transcript.
  const stopAudioInput = useCallback(() => {
    try {
      workletNodeRef.current?.disconnect();
    } catch {
      // ignore — node may already be detached
    }
    workletNodeRef.current = null;
    try {
      sourceNodeRef.current?.disconnect();
    } catch {
      // ignore
    }
    sourceNodeRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop();
        } catch {
          // ignore
        }
      }
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
    }
    wsRef.current = null;
  }, []);

  // Nuclear shutdown — used by the close/error paths and unmount.
  const teardown = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (finalTimerRef.current) {
      clearTimeout(finalTimerRef.current);
      finalTimerRef.current = null;
    }
    awaitingFinalRef.current = false;
    stopAudioInput();
    closeSocket();
    setState("idle");
    // Report the audio once: teardown can run more than once per session.
    const frames = framesSentRef.current;
    framesSentRef.current = 0;
    if (frames > 0) {
      void recordUsage({
        audioSeconds: (frames * FRAME_SAMPLES) / TARGET_SAMPLE_RATE,
      }).catch(() => undefined);
    }
  }, [stopAudioInput, closeSocket, recordUsage]);

  const stop = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      teardown();
      return;
    }

    // Release the mic immediately — no more audio frames after this.
    stopAudioInput();

    // Tell the server to commit the buffered audio. The server then
    // processes it and emits `*.input_audio_transcription.completed`.
    try {
      ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    } catch {
      teardown();
      return;
    }

    // Move into the "processing" state and wait for the completed
    // event before closing the socket — closing first races the
    // server response and loses the final transcript.
    awaitingFinalRef.current = true;
    setState("processing");

    finalTimerRef.current = setTimeout(() => {
      if (awaitingFinalRef.current) {
        awaitingFinalRef.current = false;
        teardown();
      }
    }, FINAL_TRANSCRIPT_TIMEOUT_MS);
  }, [teardown, stopAudioInput]);

  const start = useCallback(async () => {
    if (state !== "idle") return;
    setError(null);
    setState("listening");

    try {
      // 1. Mint ephemeral OpenAI token (server-side action).
      const token = await createToken({});
      // A deployment with dictation off answers with a typed refusal, not
      // an error (a stale tab can still press a mic the flag has since hidden).
      if ("disabled" in token) {
        throw new Error(token.message);
      }

      // 2. Open WebSocket using the secret as a subprotocol entry.
      // The session config (transcription mode, model, no VAD) is
      // attached to the secret server-side, so we don't need to send
      // a `session.update` here.
      const ws = new WebSocket(REALTIME_URL, [
        "realtime",
        `openai-insecure-api-key.${token.clientSecret}`,
      ]);
      wsRef.current = ws;

      const wsOpenPromise = new Promise<void>((resolve, reject) => {
        const onOpen = () => {
          ws.removeEventListener("open", onOpen);
          ws.removeEventListener("error", onErr);
          resolve();
        };
        const onErr = () => {
          ws.removeEventListener("open", onOpen);
          ws.removeEventListener("error", onErr);
          reject(new Error("Failed to open transcription WebSocket"));
        };
        ws.addEventListener("open", onOpen);
        ws.addEventListener("error", onErr);
      });

      ws.addEventListener("message", (event: MessageEvent<string>) => {
        try {
          const message = JSON.parse(event.data) as RealtimeMessage;
          handleRealtimeMessage(message);
        } catch {
          // Non-JSON or unrecognised payload — ignore.
        }
      });

      ws.addEventListener("close", () => {
        teardown();
      });

      ws.addEventListener("error", () => {
        const message = "Voice transcription connection error";
        setError(message);
        onErrorRef.current?.(message);
        teardown();
      });

      await wsOpenPromise;

      // 3. Capture mic + spin up AudioContext at the target rate.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      audioContextRef.current = audioContext;
      // Some browsers (notably Safari) ignore the requested sampleRate
      // and pin the AudioContext to the device default — the worklet
      // resamples internally so this still works, just slightly less
      // accurately.
      try {
        await audioContext.audioWorklet.addModule(getWorkletUrl());
      } catch (err) {
        teardown();
        throw err;
      }

      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const worklet = new AudioWorkletNode(
        audioContext,
        "pcm-downsample-processor",
        {
          processorOptions: {
            targetSampleRate: TARGET_SAMPLE_RATE,
            frameSamples: FRAME_SAMPLES,
          },
        },
      );
      workletNodeRef.current = worklet;

      worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const audio = arrayBufferToBase64(event.data);
        try {
          ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
          framesSentRef.current += 1;
        } catch {
          // ignore — close handler will tear down
        }
      };

      source.connect(worklet);
      // The worklet doesn't render audio out, but Chrome stops pulling
      // input when the graph isn't connected to the destination, so we
      // route a silent path.
      const silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      worklet.connect(silentGain).connect(audioContext.destination);

      // 4. Auto-stop fuse.
      autoStopTimerRef.current = setTimeout(() => {
        stop();
      }, maxSeconds * 1000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not start voice input";
      setError(message);
      onErrorRef.current?.(message);
      teardown();
    }

    function handleRealtimeMessage(message: RealtimeMessage) {
      switch (message.type) {
        case "conversation.item.input_audio_transcription.delta": {
          const delta = message.delta;
          if (typeof delta === "string" && delta.length > 0) {
            onDeltaRef.current?.(delta);
          }
          break;
        }
        case "conversation.item.input_audio_transcription.completed": {
          const transcript = message.transcript;
          if (typeof transcript === "string" && transcript.length > 0) {
            onFinalRef.current?.(transcript);
          }
          // The final transcript is the cue we were waiting for —
          // tear down so the UI returns to idle.
          if (awaitingFinalRef.current) {
            awaitingFinalRef.current = false;
            if (finalTimerRef.current) {
              clearTimeout(finalTimerRef.current);
              finalTimerRef.current = null;
            }
            teardown();
          }
          break;
        }
        case "error": {
          const errorMessage =
            message.error?.message ?? "Voice transcription error";
          setError(errorMessage);
          onErrorRef.current?.(errorMessage);
          break;
        }
        default:
          break;
      }
    }
  }, [createToken, state, maxSeconds, stop, teardown]);

  // Tear down on unmount so a half-open WebSocket / mic stream doesn't
  // leak when the component is removed.
  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  return {
    start,
    stop,
    state,
    isActive: state !== "idle",
    error,
    isEnabled,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  // 0x8000 chunks keep this under most engine string-arg limits.
  const chunk = 0x8000;
  const chunkCount = Math.ceil(bytes.length / chunk);
  const binary = Array.from({ length: chunkCount }, (_, idx) => {
    const start = idx * chunk;
    const slice = bytes.subarray(start, Math.min(start + chunk, bytes.length));
    return String.fromCharCode(...slice);
  }).join("");
  return btoa(binary);
}
